/**
 * Fall 2026 live working sessions.
 *
 * Dates, titles, descriptions and the time are the founder's, verbatim.
 * Descriptions (Oct 3, 2026) also ride in the day-before and morning-of
 * reminder emails; backend/server.py WEBINAR_SESSIONS mirrors this file and a
 * test fails if the two ever differ. Nothing here is
 * inferred: a calendar entry with a guessed time is worse than none, because
 * the attendee trusts it and misses the session.
 *
 * A one-off move off Thursday carries a `dayNote`. The series copy still says
 * Thursdays, because it is a Thursday series; the note is what tells a visitor
 * that this particular week is different. Drop the field when the exception
 * passes.
 */

/** The series' full name, as the founder writes it. */
export const SERIES_NAME = "The Clarity Briefing Series, LIVE | Powered by Web3 Ready CLARITY";

// "HH:MM" in Eastern Time, 24-hour. Sessions run noon ET for one hour.
export const START_TIME_ET = "12:00";
export const DURATION_MINUTES = 60;
/**
 * Deliberately empty. Where a session is hosted is decided session by session
 * (founder's call), and the join link goes to registrants by email, so no
 * platform and no link belongs in a calendar entry, a page, or an HTML source
 * view.
 */
export const JOIN_URL = "";

/** How the time is stated everywhere on the page. */
export const TIME_LABEL = "Noon ET, one hour";

/**
 * A session's start time and time label, falling back to the series defaults.
 * A one-off session that runs at a different hour carries its own `startTimeEt`
 * ("HH:MM" ET, 24-hour) and `timeLabel`; everything else stays at noon.
 */
export const startTimeFor = (session) => session?.startTimeEt || START_TIME_ET;
export const timeLabelFor = (session) => session?.timeLabel || TIME_LABEL;

export const SESSIONS = [
  {
    date: "2026-09-25",
    label: "Sept 25",
    // Broadcast trouble on Thursday Sept 24: the soft launch briefing moved to
    // Friday, same noon ET start. The note stays now that the session is
    // behind us, because it is a true fact about a session still listed in the
    // schedule. The announcement-bar copy that rode with it (`resumesNote`) is
    // gone: it was only ever for the week this session was still upcoming.
    dayNote: "Friday",
    // Delivered. The founder's own signal, not a guess from the clock: the
    // session is behind us and must stop being promoted, whatever a server's
    // time zone or a stale prerender thinks. The time-based check below still
    // covers every future session automatically.
    delivered: true,
    title: "The January 18 Problem: Treasury's New Stablecoin Licensing Rule and What to Do Before 2027",
    description: "A licensing rule with a hard date is a deadline, not a headline. This session covered what Treasury's new stablecoin licensing rule changes, who it reaches, and what to do before the 2027 clock runs out. Delivered live; covered for everyone registered for the Fall run.",
  },
  {
    date: "2026-10-01",
    label: "Oct 1",
    title: "Regulation Crypto Assets: Inside the SEC's Proposed Framework",
    description: "A proposed framework is the one window where the rules can still be read before they harden. This session walked through what the SEC's framework proposes, where it is still open, and the questions worth asking while it is still a proposal. Delivered live; covered for everyone registered for the Fall run.",
  },
  {
    date: "2026-10-08",
    label: "Oct 8",
    title: "The CLARITY Act: Where Market Structure Actually Stands",
    description: "Market structure decides which rules apply to your product, your partners, and your disclosures. This session separates the headlines from where the CLARITY Act actually stands, and what to plan for now.",
  },
  {
    date: "2026-10-15",
    label: "Oct 15",
    title: "Crypto Custody Without a Custodian: The SEC's Proposal Puts the Burden on Advisers and Funds. How to Prove You Were Ready.",
    description: "The SEC's custody proposal shifts the burden to advisers and funds. When someone asks how you were prepared, you will need to show your work. This session covers what the proposal asks and how to document readiness now.",
  },
  {
    date: "2026-11-05",
    label: "Nov 5",
    // The closing session moved from Thursday, Oct 29 (1:00 PM) to Thursday,
    // Nov 5, 2026, running 12:00-1:15 PM ET (founder's call, Feb 2026). It is
    // back at the series NOON start, so there is no startTimeEt override; the
    // two exceptions this week are DURATION (75 minutes, carried by
    // durationMinutes) and the OFFSET (Nov 5 is after US DST ends Nov 1 2026,
    // so it is EST -05:00; offsetFor() derives that from the date). The
    // "This week only" note carries the one-off 12:00-1:15 PM span. Drop
    // dayNote / durationMinutes / timeLabel if the finale ever returns to the
    // plain noon-hour cadence.
    dayNote: "Thursday, Nov 5",
    durationMinutes: 75,
    timeLabel: "12:00 to 1:15 PM ET",
    title: "Your Year-End Readiness Roadmap: A Real Scenario, Mapped Live",
    description: "Knowing the rules is different from spotting the issues in your own facts. Watch one real scenario go through the CLARITY engine on screen, then see the full series tied together into a practical roadmap for improving your digital assets regulatory readiness.",
  },
];

/** Sessions run on Thursdays; the Oct 22 gap is intentional, not an omission. */
export const SCHEDULE_NOTE = "No session Oct 22.";

/**
 * Eastern offset, per session. US daylight saving ends Nov 1 2026, so sessions
 * on or before Oct 31 2026 are EDT (-04:00) and sessions on Nov 1 2026 or later
 * are EST (-05:00). Date-driven so a new date picks the right offset by itself;
 * the Nov 5 finale is the first session that lands in EST. Re-check when 2027
 * dates are added.
 */
const offsetFor = (session) => ((session?.date || "") >= "2026-11-01" ? "-05:00" : "-04:00");

/** A session's length in minutes: its own `durationMinutes` or the series hour. */
const durationFor = (session) => session?.durationMinutes || DURATION_MINUTES;

/** When a session is over. Uses the session's own offset and duration. */
export const sessionEnd = (session) =>
  new Date(
    new Date(`${session.date}T${startTimeFor(session)}:00${offsetFor(session)}`).getTime()
    + durationFor(session) * 60000,
  );

/** Already delivered. A finished session must never be promoted as next. */
export const hasFinished = (session, now = new Date()) =>
  session.delivered === true || sessionEnd(session) < now;

/**
 * The next session that has not finished, or the last one if the run is over.
 *
 * Rolls over when the session ENDS, not at midnight: the hour after a session
 * is exactly when someone arrives from the replay or a share, and telling them
 * the next briefing is the one that just happened costs the registration.
 */
export const nextSession = (now = new Date()) =>
  SESSIONS.find((s) => !hasFinished(s, now)) || SESSIONS[SESSIONS.length - 1];

const utcStamp = (date, timeEt, offset, addMinutes = 0) => {
  const start = new Date(`${date}T${timeEt}:00${offset}`);
  const at = new Date(start.getTime() + addMinutes * 60000);
  return at.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
};

export const googleCalendarUrl = (session) => {
  if (!START_TIME_ET) return null;
  const startEt = startTimeFor(session);
  const offset = offsetFor(session);
  const dur = durationFor(session);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `The CLARITY Briefing: ${session.title}`,
    dates: `${utcStamp(session.date, startEt, offset)}/${utcStamp(session.date, startEt, offset, dur)}`,
    details: `Hosted by Dr. Tonya M. Evans. ${JOIN_URL ? `Join: ${JOIN_URL}` : "Runs live online. The join link is emailed to registrants."}`,
    location: JOIN_URL || "Online",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export const icsFor = (session) => {
  if (!START_TIME_ET) return null;
  const startEt = startTimeFor(session);
  const offset = offsetFor(session);
  const dur = durationFor(session);
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Web3 Ready CLARITY//The CLARITY Briefing//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:w3rc-${session.date}@web3readyclarity.com`,
    `DTSTAMP:${utcStamp(session.date, startEt, offset)}`,
    `DTSTART:${utcStamp(session.date, startEt, offset)}`,
    `DTEND:${utcStamp(session.date, startEt, offset, dur)}`,
    `SUMMARY:The CLARITY Briefing: ${session.title}`,
    `DESCRIPTION:Hosted by Dr. Tonya M. Evans, digital asset attorney and regulatory strategist.${JOIN_URL ? `\\nJoin: ${JOIN_URL}` : "\\nRuns live online. The join link is emailed to registrants."}`,
    `LOCATION:${JOIN_URL || "Online"}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
};
